import React from 'react';
import I18nWrapper, { getMessages } from './I18nWrapper';
import moment from 'moment';
import { IntlProvider, FormattedMessage, addLocaleData } from 'react-intl';
import SDK from 'radware-client-sdk';
import { whiteLabel, i18n, dateShortTimeFormat, dateFormat } from '../state/configuration/general-app-configuration';
import { formatNumber, shortFormatNumber } from 'comp-atomic/utils/formattingUtils';
import { supportedLocales } from './i18n.config';

import en from 'react-intl/locale-data/en';
import localeData_en from './data_en';
addLocaleData([...en]);

const LocalizationHelper = SDK.RequestBuilderUtils;
export const LOCALE = supportedLocales.includes(i18n.locale) ? i18n.locale : 'en';

export const MESSAGES = { en: localeData_en };
export const storageMessages = window && window.i18n && getMessages();

const localeMessages = { ...MESSAGES[LOCALE], ...((storageMessages && storageMessages[LOCALE]) || {}) };
const translateMessages = { [LOCALE]: localeMessages };

supportedLocales
    .filter(locale => locale !== 'en')
    .map(locale => {
        try {
            const localeData = require('react-intl/locale-data/' + locale);
            addLocaleData([...localeData]);

            const localeText = require('./data_' + locale);
            MESSAGES[locale] = { ...localeData_en, ...localeText };
        } catch (e) {
            console.log('Failed loading locale data: ' + locale);
        }
    });

const aux = { br: '\n' };
const jsxAux = {
    br: <br />,
    supportEmailAnchor: <a href={`mailto: ${whiteLabel.supportEmail}`}>{whiteLabel.supportEmail}</a>,
};

//FIXME: Requires fixing: {brandName}, {providerName}, {br}, {minValue}, {maxValue}, \/, yyyy-MM-ddTHH

export default class I18nUtils {
    static translate(key, defValue, params) {
        const messages = window.shffdaq ? translateMessages : MESSAGES;
        return LocalizationHelper.getFormatMessage(LOCALE, messages, key, defValue || key, { ...params, ...whiteLabel, ...aux });
    }

    static getFormattedMessage(key, defValue, params) {
        return I18nUtils.wrapForDev(
            key,
            <FormattedMessage id={key} defaultMessage={defValue || key} values={{ ...params, ...whiteLabel, ...jsxAux }} />
        );
    }

    static getFormattedDuration(duration, withSuffix) {
        if (!moment.isDuration(duration)) {
            duration = moment.duration(duration);
        }
        return duration.locale(LOCALE).humanize(withSuffix === undefined ? true : withSuffix);
    }

    static getFormattedDate(dateTime, dateOrTimeFormat) {
        if (!dateTime) {
            return translate('N_A');
        }
        return moment(dateTime).format(dateOrTimeFormat || dateFormat);
    }

    static getDurationDescriptors(dateTime) {
        let connector = null,
            duration = null;
        if (!dateTime || !dateTime.duration) {
            return { connector, duration };
        }
        if (!dateTime.end) {
            // connector = getFormattedMessage('during_last');
            connector = getFormattedMessage('during');
            duration = getFormattedDuration(dateTime.duration, false);
        } else {
            connector = getFormattedMessage('between');
            // const format = 'MMM DD, YYYY HH:mm';
            const to = moment(dateTime.end);
            const dur = moment.duration(dateTime.duration);
            duration = `${moment(to)
                .subtract(dur)
                .format(dateShortTimeFormat)} - ${moment(to).format(dateShortTimeFormat)}`;
        }
        return { connector, duration };
    }

    static getFormattedNumber(number, unit, showUnit) {
        return formatNumber(number, LOCALE, unit, showUnit);
    }

    static getShortFormattedNumber(number, unit, showUnit) {
        return shortFormatNumber(number, LOCALE, unit, showUnit);
    }

    static i18nWrap(comp) {
        if (!process || !process.env || process.env.NODE_ENV !== 'test') {
            return comp;
        }

        try {
            const messages = window.i18n ? localeMessages : MESSAGES[LOCALE];
            return (
                <IntlProvider locale={LOCALE} key={LOCALE} messages={messages}>
                    {comp}
                </IntlProvider>
            );
        } catch (e) {
            console.log('Error while wrapping component for i18n testing: ' + e);
            return comp;
        }
    }

    static wrapForDev(key, translation) {
        if (!window || !window.i18n) {
            return translation;
        }
        try {
            return <I18nWrapper i18nKey={key} translation={translation} locale={LOCALE} messages={MESSAGES} />;
        } catch (e) {
            console.log('Error while wrapping translation for i18n/TW editing: ' + e);
            return translation;
        }
    }
}

export const translate = I18nUtils.translate;
export const getFormattedMessage = I18nUtils.getFormattedMessage;
export const getFormattedDuration = I18nUtils.getFormattedDuration;
export const getFormattedDate = I18nUtils.getFormattedDate;
export const getDurationDescriptors = I18nUtils.getDurationDescriptors;
export const getFormattedNumber = I18nUtils.getFormattedNumber;
export const getShortFormattedNumber = I18nUtils.getShortFormattedNumber;
export const i18nWrap = I18nUtils.i18nWrap;

export const crudLabels = {
    unAuthorizedLabel: translate('access_denied'),
    saveLabel: translate('save'),
    saveConfirmLabel: translate('saveConfirm'),
    deleteLabel: translate('delete'),
    deleteConfirmLabel: translate('deleteConfirm'),
    cancelLabel: translate('cancel'),
    discardLabel: translate('discardChanges'),
    confirmCancelLabel: translate('leaveIt'),
};

export const dualListLabels = {
    select: translate('select'),
    selectAll: translate('selectAll'),
    unSelect: translate('unSelect'),
    unSelectAll: translate('unSelectAll'),
    available: translate('available'),
    selected: translate('selected'),
};

export const validationRulesHint = (error, key) => {
    return <span className={`validation-error ${key || ''}`}>{key ? getFormattedMessage('validation_' + key, error) : error}</span>;
};

export const getEntitiesSummary = (entities, visible = 2) => {
    if (!entities || !entities.length) {
        return '';
    }
    return entities.length <= visible
        ? entities.join(', ')
        : translate('MoreItems', '...', {
              value: entities.slice(0, visible).join(', '),
              remainder: entities.length - visible,
          });
};

export const globalValidationRules = new SDK.ApplicationProfile().getValidationRules(validationRulesHint);
